import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgReportsApi } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';

/**
 * Parent Attendance Center-wise Report — org tier, read-only. "Center"
 * means school here (matches the real `state-reports.
 * parent-attendance-center-wise-report` shape) — per-school x per-class
 * guardian TTL/P/A matrix. Column axis is the real union of distinct
 * class names across the schools in scope, not real legacy's own
 * hardcoded "first N classes of one template school" hack.
 */
export function OrgParentAttendanceCenterWisePage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-parent-attendance-center-wise'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const params = {
    schoolId: schoolId || undefined,
    stateId: stateId === '' ? undefined : stateId,
    startDate,
    endDate,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-parent-attendance-center-wise', params],
    queryFn: () => OrgReportsApi.parentAttendanceCenterWise(params),
  });

  const rows = data?.rows ?? [];
  const classNames = data?.classNames ?? [];

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportParentAttendanceCenterWise(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Parent Attendance – Center-wise Report"
        description="Guardian check-in totals per class, per school (center), over a date range."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th rowSpan={2} className="sticky left-0 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 align-bottom">
                  Center (School)
                </th>
                {classNames.map((c) => (
                  <th key={c} colSpan={3} className="border-l border-slate-200 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
              <tr>
                {classNames.map((c) => (
                  <Fragment key={c}>
                    <th className="border-l border-slate-200 px-2 py-2 text-right text-xs font-medium text-slate-400">TTL</th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-green-600">P</th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-red-600">A</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={1 + classNames.length * 3} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={1 + classNames.length * 3} className="px-4 py-12 text-center text-slate-400">
                    No schools found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.schoolId} className="hover:bg-slate-50">
                    <td className="sticky left-0 bg-white px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                    {classNames.map((c) => {
                      const cell = r.cells[c] ?? { total: 0, present: 0, absent: 0 };
                      return (
                        <Fragment key={c}>
                          <td className="border-l border-slate-100 px-2 py-3 text-right text-slate-600">{cell.total || ''}</td>
                          <td className="px-2 py-3 text-right text-green-700">{cell.present || ''}</td>
                          <td className="px-2 py-3 text-right text-red-700">{cell.absent || ''}</td>
                        </Fragment>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
